// setting_definition registry [02 §11; 04 §3.2; 05 §16; 06 §2 settings; 07 §1.2; BUILD-PLAN §9].
// 02 names win (`costing.allowance.fabric_pct`); values are JSON (decimals as strings, exact).
import { z } from 'zod';
import type { SettingScope, SettingValueType } from '../enums/types-platform.ts';
import { READINESS_GATE_MODE } from '../enums/types-ops.ts';
import { isId } from '../kernel/id.ts';
import { ITEM_CODE_WIDTHS } from '../kernel/item-code.ts';
import { YARD_IN_METRES } from '../units/qty.ts';

const decimal = z.string().regex(/^-?\d+(\.\d+)?$/);
const pctValue = decimal.refine((v) => Math.abs(Number(v)) <= 999.9999, {
  message: 'pct outside numeric(7,4)',
});
const moneyValue = z.object({ amount: decimal, currency: z.string().regex(/^[A-Z]{3}$/) });

export type SettingMoneyValue = z.infer<typeof moneyValue>;

type ValueOf<T extends SettingValueType> = T extends 'int'
  ? number
  : T extends 'numeric' | 'pct' | 'text'
    ? string
    : T extends 'money'
      ? SettingMoneyValue
      : T extends 'bool'
        ? boolean
        : unknown;

export interface SettingDefinition<
  K extends string = string,
  T extends SettingValueType = SettingValueType,
> {
  readonly key: K;
  readonly valueType: T;
  readonly unit: string | null;
  readonly scopes: readonly SettingScope[];
  readonly default: ValueOf<T>;
  readonly allowedValues: readonly string[] | null;
  readonly ownerModule: string;
  readonly labelKey: string;
  /** Item-code widths etc.: edits refused once go-live has passed [BR-SET-004]. */
  readonly frozenAfterGoLive: boolean;
  /** Constants exposed for display only (edit → 409 FROZEN_SETTING). */
  readonly readOnly: boolean;
  /** Changing the value needs a fresh step-up (e.g. `finance.mode`). */
  readonly stepUp: boolean;
  readonly schema: z.ZodType<ValueOf<T>>;
}

interface Opts {
  readonly unit?: string;
  readonly allowed?: readonly string[];
  readonly frozen?: boolean;
  readonly readOnly?: boolean;
  readonly stepUp?: boolean;
  readonly schema?: z.ZodType;
}

function schemaFor(t: SettingValueType, allowed?: readonly string[]): z.ZodType {
  switch (t) {
    case 'int':
      return z.number().int();
    case 'numeric':
      return decimal;
    case 'pct':
      return pctValue;
    case 'money':
      return moneyValue;
    case 'text':
      return allowed ? z.enum(allowed as [string, ...string[]]) : z.string().min(1).max(500);
    case 'bool':
      return z.boolean();
    default:
      return z.unknown();
  }
}

function def<const K extends string, const T extends SettingValueType>(
  key: K,
  valueType: T,
  scopes: readonly SettingScope[],
  dflt: ValueOf<T>,
  opts: Opts = {},
): SettingDefinition<K, T> {
  const schema = (opts.schema ?? schemaFor(valueType, opts.allowed)) as z.ZodType<ValueOf<T>>;
  schema.parse(dflt);
  return Object.freeze({
    key,
    valueType,
    unit: opts.unit ?? null,
    scopes: Object.freeze([...scopes]),
    default: dflt,
    allowedValues: opts.allowed ? Object.freeze([...opts.allowed]) : null,
    ownerModule: key.split('.')[0] ?? key,
    labelKey: `settings.${key}`,
    frozenAfterGoLive: opts.frozen ?? false,
    readOnly: opts.readOnly ?? false,
    stepUp: opts.stepUp ?? false,
    schema,
  });
}

const cad = (amount: string): SettingMoneyValue => ({ amount, currency: 'CAD' });
const G = ['global'] as const;
const GC = ['global', 'customer'] as const;
const GS = ['global', 'supplier'] as const;
/** Bounds of the CR-BG2c finance settings (an admin typo cannot widen a tolerance or a window without limit). */
const MAX_TOLERANCE_MINOR = 1_000_000;
const MAX_SETTING_DAYS = 366;
/** CR-BG2d: posting retry bounds (BR-PST-011): back-off base at most a day, at most 100 attempts. */
const MAX_RETRY_BASE_MINUTES = 1_440;
const MAX_POSTING_ATTEMPTS = 100;
/** CR-BG2e: bounds of the Sage bridge / close / NL assistant settings. */
const MAX_EXPORT_DISABLED_RULES = 40;
const MAX_CLOSE_CHECK_CODES = 100;
const MAX_NL_MONTHLY_TOKENS = 1_000_000_000;
/** CR-BG2f: bounds of the D2C settings (a typo cannot hide the whole web stock or lift the refund control). */
const MAX_D2C_STOCK_BUFFER = 1_000;
const MAX_REFUND_THRESHOLD_MINOR = 10_000_000_000;
/** A partners carrier id (bigint identity as a decimal string, `isId`) or null = not set. */
const carrierIdOrNull = z
  .string()
  .refine(isId, { message: 'invalid id' })
  .nullable();
/** Posting rule codes (P01…P99), as `finance.journal_entry.rule_code`. */
const ruleCode = z.string().regex(/^P\d{2}$/);
/** Close check codes (`JNL_POSTED`, `BRIDGE` …): a controller's per-code `blocking` / `enabled` override. */
const closeCheckCode = z.string().regex(/^[A-Z][A-Z0-9_]{1,39}$/);
const closeCheckConfig = z
  .record(closeCheckCode, z.strictObject({ blocking: z.boolean().optional(), enabled: z.boolean().optional() }))
  .refine((v) => Object.keys(v).length <= MAX_CLOSE_CHECK_CODES, { message: 'too many check codes' });
const width =(part: keyof typeof ITEM_CODE_WIDTHS) =>
  def(`itemcode.width.${part}`, 'int', G, ITEM_CODE_WIDTHS[part], { frozen: true, readOnly: true });

export const SETTINGS = {
  // costing [04 §3.2; MB D9]
  'costing.allowance.fabric_pct': def(
    'costing.allowance.fabric_pct',
    'pct',
    ['global', 'customer', 'supplier'],
    '6',
  ),
  'costing.allowance.variant2_pct': def('costing.allowance.variant2_pct', 'pct', G, '2'),
  'costing.allowance.variant3_pct': def('costing.allowance.variant3_pct', 'pct', G, '5'),
  'costing.allowance.trim_pct': def('costing.allowance.trim_pct', 'pct', GC, '0'),
  'costing.presenter_per_unit': def('costing.presenter_per_unit', 'money', GC, cad('1.25')),
  'costing.label_polybag_per_unit': def('costing.label_polybag_per_unit', 'money', GC, cad('0.80')),
  'costing.testing_per_unit': def('costing.testing_per_unit', 'money', GC, cad('0.10')),
  'costing.inspection_per_unit': def('costing.inspection_per_unit', 'money', GC, cad('0')),
  'costing.markup_pct': def('costing.markup_pct', 'pct', GC, '50'),
  'costing.customer_discount_pct': def('costing.customer_discount_pct', 'pct', ['customer'], '0'),
  'costing.commission_pct': def('costing.commission_pct', 'pct', GC, '1.00'),
  'costing.min_net_on_cost_pct': def('costing.min_net_on_cost_pct', 'pct', GC, '80'),
  'costing.variance_reason_threshold_pct': def(
    'costing.variance_reason_threshold_pct',
    'pct',
    G,
    '5',
  ),
  'costing.revaluation_min_unit_delta': def(
    'costing.revaluation_min_unit_delta',
    'money',
    G,
    cad('0.01'),
  ),
  'costing.default_rate_qty': def('costing.default_rate_qty', 'int', GC, 1000, { unit: 'pcs' }),
  // labels / inventory / development / programmes / samples / dpp [04 §3.2]
  'labels.default_care_size': def('labels.default_care_size', 'text', GC, 'Std-23'),
  'labels.care_spare_pct': def('labels.care_spare_pct', 'pct', ['customer'], '0'),
  'inventory.default_warehouse': def('inventory.default_warehouse', 'text', G, 'MAIN'),
  'inventory.remnant_threshold_m': def('inventory.remnant_threshold_m', 'numeric', G, '2.0', {
    unit: 'm',
  }),
  'development.readiness_gate_mode': def('development.readiness_gate_mode', 'text', GC, 'warn', {
    allowed: READINESS_GATE_MODE.values,
  }),
  'programmes.at_risk_days': def('programmes.at_risk_days', 'int', GC, 7, { unit: 'days' }),
  'programmes.escalate_after_days': def('programmes.escalate_after_days', 'int', GC, 3, {
    unit: 'days',
  }),
  'samples.reminder_days': def('samples.reminder_days', 'int', G, 2, { unit: 'days' }),
  'dpp.required_elements': def('dpp.required_elements', 'json', G, [] as unknown, {
    schema: z.array(z.string().min(1)),
  }),
  'itemcode.width.style': width('style'),
  'itemcode.width.group': width('group'),
  'itemcode.width.season': width('season'),
  'itemcode.width.colour': width('colour'),
  'itemcode.width.size': width('size'),
  'uom.yard_in_m': def('uom.yard_in_m', 'numeric', G, YARD_IN_METRES, {
    readOnly: true,
    frozen: true,
  }),
  // orders / PO import / cutting / sewing / FG / labels / shipping [06 §2]
  'po_import.price_tolerance_pct': def(
    'po_import.price_tolerance_pct',
    'pct',
    ['customer'],
    '0.00',
  ),
  'po_import.lineup_qty_tolerance_pct': def(
    'po_import.lineup_qty_tolerance_pct',
    'pct',
    ['customer'],
    '0',
  ),
  'orders.overship_tolerance_pct': def('orders.overship_tolerance_pct', 'pct', ['customer'], '5'),
  // CR-BG2c (W2c.orders R-SET-1): revision / entry price tolerance (was po_import.price_tolerance_pct) and the written
  // customer confirmation duty (was a constant keyed by the PO parser code, BR-POI-023).
  'orders.price_tolerance_pct': def('orders.price_tolerance_pct', 'pct', ['customer'], '0.00'),
  'orders.confirmation_required': def('orders.confirmation_required', 'bool', ['customer'], false),
  'orders.overship_tolerance_basis': def(
    'orders.overship_tolerance_basis',
    'text',
    ['customer'],
    'order_total',
    {
      allowed: ['per_sku', 'order_total'],
    },
  ),
  'cutting.fabric_allowance_pct': def(
    'cutting.fabric_allowance_pct',
    'pct',
    ['global', 'customer', 'style', 'fabric'],
    '6',
  ),
  'cutting.overcut_pct': def('cutting.overcut_pct', 'pct', GC, '5'),
  'cutting.requirement_rounding': def(
    'cutting.requirement_rounding',
    'text',
    G,
    'ceil_whole_metre',
  ),
  'cutting.require_distinct_confirmers': def(
    'cutting.require_distinct_confirmers',
    'bool',
    G,
    true,
  ),
  'sewing.delivery_default_days': def('sewing.delivery_default_days', 'int', G, 15, {
    unit: 'days',
  }),
  'fg_receiving.over_receipt_tolerance_pct': def(
    'fg_receiving.over_receipt_tolerance_pct',
    'pct',
    ['contractor'],
    '0',
  ),
  'shipping.booking_lead_days': def('shipping.booking_lead_days', 'int', ['customer'], 21, {
    unit: 'days',
  }),
  'shipping.skid_tare_kg': def('shipping.skid_tare_kg', 'numeric', ['customer'], '0', {
    unit: 'kg',
  }),
  'shipping.skids_per_container_default': def('shipping.skids_per_container_default', 'int', G, 1),
  // CR-BG2d (W2d.shipping R-CORE-SHP-2): BR-SHP-015 delivery-notice lead in working days per customer (0 = no notice
  // duty; QVC DE 6 is customer data) and the GS1 company prefix of SSCC numbers (default: the GS1 documentation prefix
  // until KIMCO's own is known, Q-SHP-01).
  'shipping.notice_lead_working_days': def('shipping.notice_lead_working_days', 'int', ['customer'], 0, {
    unit: 'working days',
    schema: z.number().int().min(0).max(MAX_SETTING_DAYS),
  }),
  'shipping.gs1_company_prefix': def('shipping.gs1_company_prefix', 'text', G, '0614141', {
    schema: z.string().regex(/^\d{7,10}$/),
  }),
  // purchasing / ETA / testing / planning [05 §16]
  'purchasing.issue_without_approval_cad': def(
    'purchasing.issue_without_approval_cad',
    'money',
    G,
    cad('10000.00'),
  ),
  'purchasing.over_receipt_tolerance_pct': def(
    'purchasing.over_receipt_tolerance_pct',
    'json',
    GS,
    { fabric: '2', trim: '5' } as unknown,
    { schema: z.object({ fabric: pctValue, trim: pctValue }) },
  ),
  'purchasing.confirm_days': def('purchasing.confirm_days', 'int', GS, 5, { unit: 'days' }),
  'purchasing.on_time_grace_days': def('purchasing.on_time_grace_days', 'int', GS, 3, {
    unit: 'days',
  }),
  'purchasing.transit_days.sea': def('purchasing.transit_days.sea', 'int', GS, 30, {
    unit: 'days',
  }),
  'purchasing.transit_days.air': def('purchasing.transit_days.air', 'int', GS, 5, { unit: 'days' }),
  'purchasing.transit_days.road': def('purchasing.transit_days.road', 'int', GS, 7, {
    unit: 'days',
  }),
  'purchasing.transit_days.courier': def('purchasing.transit_days.courier', 'int', GS, 3, {
    unit: 'days',
  }),
  'purchasing.port_clearance_days': def(
    'purchasing.port_clearance_days',
    'int',
    ['global', 'warehouse'],
    3,
    { unit: 'days' },
  ),
  'purchasing.customs_to_warehouse_days': def(
    'purchasing.customs_to_warehouse_days',
    'int',
    ['global', 'warehouse'],
    2,
    {
      unit: 'days',
    },
  ),
  'purchasing.feed_stale_hours': def(
    'purchasing.feed_stale_hours',
    'int',
    ['global', 'carrier'],
    48,
    { unit: 'hours' },
  ),
  'purchasing.missing_milestone_grace_days': def(
    'purchasing.missing_milestone_grace_days',
    'int',
    ['global', 'carrier'],
    2,
    {
      unit: 'days',
    },
  ),
  'testing.bulk_required': def(
    'testing.bulk_required',
    'bool',
    ['global', 'supplier', 'customer'],
    true,
  ),
  'testing.turnaround_days': def(
    'testing.turnaround_days',
    'int',
    ['global', 'supplier', 'customer'],
    14,
    { unit: 'days' },
  ),
  'planning.horizon_weeks': def('planning.horizon_weeks', 'int', G, 12, { unit: 'weeks' }),
  'planning.run_time': def('planning.run_time', 'text', G, '06:00', {
    schema: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  }),
  'planning.requirement_rounding': def(
    'planning.requirement_rounding',
    'text',
    G,
    'ceil_whole_unit',
  ),
  'planning.cut_to_ship_days': def('planning.cut_to_ship_days', 'int', GC, 21, { unit: 'days' }),
  'planning.allocation_priority': def('planning.allocation_priority', 'json', G, [] as unknown, {
    schema: z.array(z.string().min(1)),
  }),
  'planning.aging_bucket_edges_days': def(
    'planning.aging_bucket_edges_days',
    'json',
    G,
    [90, 180, 365] as unknown,
    {
      schema: z.array(z.number().int().positive()),
    },
  ),
  'planning.aging_review_days': def('planning.aging_review_days', 'int', G, 180, { unit: 'days' }),
  'planning.aging_divert_days': def('planning.aging_divert_days', 'int', G, 540, { unit: 'days' }),
  // GL [07 BR-FX-002, BR-GL-004, BR-TAX-006] (CR-W2b-12, CR-W2b-6 R-CORE-GL-2; were named constants)
  'fx.rate_max_age_business_days': def('fx.rate_max_age_business_days', 'int', G, 5, {
    unit: 'business days',
  }),
  'fx.manual_tolerance_pct': def('fx.manual_tolerance_pct', 'pct', G, '1.0000'),
  'tax.filing_frequency': def('tax.filing_frequency', 'text', G, 'monthly', {
    allowed: ['monthly', 'quarterly', 'annual'],
  }),
  // AP [07 BR-AP-011/031/035] (CR-BG2c, W2c.ap R-CORE-AP-2; defaults = the lane's named constants). Over-invoice
  // quantity tolerance per PO category (rolls vary in length; trims and services do not) [assumption Q-AP-01].
  'ap.match.qty_tolerance_pct': def(
    'ap.match.qty_tolerance_pct',
    'json',
    GS,
    { fabric: '3', trim: '0', service: '0' } as unknown,
    { schema: z.object({ fabric: pctValue, trim: pctValue, service: pctValue }).strict() },
  ),
  'ap.match.price_tolerance_pct': def('ap.match.price_tolerance_pct', 'pct', G, '0.5'),
  'ap.match.price_tolerance_cad_minor': def('ap.match.price_tolerance_cad_minor', 'int', G, 500, {
    unit: 'CAD minor',
    schema: z.number().int().min(0).max(MAX_TOLERANCE_MINOR),
  }),
  'ap.payment_run.look_ahead_days': def('ap.payment_run.look_ahead_days', 'int', G, 7, {
    unit: 'days',
    schema: z.number().int().min(0).max(MAX_SETTING_DAYS),
  }),
  'ap.contractor_run_interval_days': def('ap.contractor_run_interval_days', 'int', G, 14, {
    unit: 'days',
    schema: z.number().int().min(1).max(MAX_SETTING_DAYS),
  }),
  // AR / bank [07 BR-AR-033, BR-BNK-003] (CR-BG2c, W2c.ar-bank R-CORE-AR-1)
  'ar.write_off_tolerance_minor': def('ar.write_off_tolerance_minor', 'int', G, 500, {
    unit: 'CAD minor',
    schema: z.number().int().min(0).max(MAX_TOLERANCE_MINOR),
  }),
  'bank.match_window_business_days': def('bank.match_window_business_days', 'int', G, 3, {
    unit: 'business days',
    schema: z.number().int().min(0).max(MAX_SETTING_DAYS),
  }),
  // posting engine [07 BR-PST-011] (CR-BG2d, W2d.posting R-CORE-PST-1; defaults = the lane's named constants, Q-PST-01)
  'posting.retry_base_minutes': def('posting.retry_base_minutes', 'int', G, 5, {
    unit: 'minutes',
    schema: z.number().int().min(1).max(MAX_RETRY_BASE_MINUTES),
  }),
  'posting.max_attempts': def('posting.max_attempts', 'int', G, 8, {
    schema: z.number().int().min(1).max(MAX_POSTING_ATTEMPTS),
  }),
  // finance [07 §1.2]: parallel until Cutover B, then live
  'finance.mode': def('finance.mode', 'text', G, 'parallel', {
    allowed: ['parallel', 'live'],
    stepUp: true,
  }),
  // CR-BG2e (W2e.close R-CORE-CLS-1, 07 §9.1 "codes as settings"): per check code `{ blocking?, enabled? }`; {} = the
  // checks' own defaults.
  'finance.close_checks': def('finance.close_checks', 'json', G, {} as unknown, { schema: closeCheckConfig }),
  // CR-BG2e (W2e.sage-bridge R-CORE-SB-1): BR-PAR-013 inventory-control export mode, BR-PAR-014 accrual export switch
  // and per-rule export switches (posting rule codes), the Sage company the vendor map is keyed by. No code literal for
  // the company (D22): empty = the controller has not set it (the bridge then uses the single company in the map).
  'bridge.ic_mode': def('bridge.ic_mode', 'text', G, 'adjustments', { allowed: ['adjustments', 'gl_only'] }),
  'bridge.export_accruals': def('bridge.export_accruals', 'bool', G, false),
  'bridge.export_disabled_rules': def('bridge.export_disabled_rules', 'json', G, [] as unknown, {
    schema: z
      .array(ruleCode)
      .max(MAX_EXPORT_DISABLED_RULES)
      .refine((v) => new Set(v).size === v.length, { message: 'duplicate rule code' }),
  }),
  'sage_bridge.company': def('sage_bridge.company', 'text', G, '', {
    schema: z.union([z.literal(''), z.string().regex(/^[A-Z0-9]{1,10}$/)]),
  }),
  // CR-BG2e (W2e.nl-assistant R-NLA-5, AI-6): monthly model-token cap of the NL assistant; 0 = no cap [assumption
  // Q-NLA-03: the amount is the client's].
  'ai.nl_reports.monthly_token_cap': def('ai.nl_reports.monthly_token_cap', 'int', G, 0, {
    unit: 'tokens',
    schema: z.number().int().min(0).max(MAX_NL_MONTHLY_TOKENS),
  }),
  // CR-BG2f (W2f.d2c-sync R4, BR-D2C-010): units held back from the web per variant (Shopify gets D2C available − buffer).
  'd2c.stock_buffer': def('d2c.stock_buffer', 'int', G, 0, {
    unit: 'units',
    schema: z.number().int().min(0).max(MAX_D2C_STOCK_BUFFER),
  }),
  // CR-BG2f (W2f.d2c-orders R-CORE-D2C-2, 07 §15.2 #2): the web parcels' carrier (partners carrier id; null = not set)
  // and the refund amount above which a refund also needs the controller (07 §15.2 #15 [assumption Q-D2C-10]).
  'd2c.carrier_id': def('d2c.carrier_id', 'json', G, null as unknown, { schema: carrierIdOrNull }),
  'd2c.refund_controller_threshold_minor': def('d2c.refund_controller_threshold_minor', 'int', G, 50_000, {
    unit: 'CAD minor',
    schema: z.number().int().min(1).max(MAX_REFUND_THRESHOLD_MINOR),
  }),
} as const;

export type SettingKey = keyof typeof SETTINGS;
export type SettingValueOf<K extends SettingKey> = (typeof SETTINGS)[K]['default'];

export const SETTING_KEYS: readonly SettingKey[] = Object.freeze(
  Object.keys(SETTINGS) as SettingKey[],
);

export function isSettingKey(value: unknown): value is SettingKey {
  return typeof value === 'string' && Object.hasOwn(SETTINGS, value);
}

export function settingDefinition(key: SettingKey): SettingDefinition {
  return SETTINGS[key] as SettingDefinition;
}

/** Legacy / chapter-03 names → registry names (02 names win; used by seed and migration). */
export const SETTING_KEY_ALIASES: Readonly<Record<string, SettingKey>> = Object.freeze({
  'costing.fabric_allowance_pct': 'costing.allowance.fabric_pct',
});
